import { getStoreLocale } from '@/data/Content/StoreLocale-Server';
import { PRODUCT_DATA_KEY, productFetcher } from '@/data/Content/_Product';
import { getLocalization } from '@/data/Localization-Server';
import { getContractIdParamFromContext, getSettings } from '@/data/Settings-Server';
import { getUser } from '@/data/User-Server';
import { getServerCacheScope } from '@/data/cache/getServerCacheScope';
import { SearchProfiles } from '@/data/constants/commonIntegrationConstants';
import { Cache } from '@/data/types/Cache';
import { Attachment, ProductType } from '@/data/types/Product';
import { getPhysicalStoreId } from '@/data/utils/commonIntegrationUtils';
import { constructRequestParamsWithPreviewToken } from '@/data/utils/constructRequestParams';
import { extractContentsArray } from '@/data/utils/extractContentsArray';
import { getCurrencyParamFromContext } from '@/data/utils/getCurrencyParamFromContext';
import { getServerSideCommon } from '@/data/utils/getServerSideCommon';
import { hasLookupValue } from '@/data/utils/hasLookupValue';
import { shrink } from '@/data/utils/keyUtil';
import { mapProductData } from '@/data/utils/mapProductData';
import { RequestParams } from 'integration/generated/query/http-client';
import { GetServerSidePropsContext } from 'next';
import { CDN_BASE_URL, MEDIA } from 'shared/custom/constants/commonConstants';
import { unstable_serialize as unstableSerialize } from 'swr';

const DATA_KEY = PRODUCT_DATA_KEY;
type ProductFetchType = {
	product: ProductType | null;
};

export const getProductSWRKey = ({
	storeId,
	lookupKey,
	lookupValue,
	catalogId,
	langId,
	physicalStoreId,
	profileName,
	marketId,
	stLocId,
	contractId,
	currency,
}: {
	storeId: string;
	lookupKey: string;
	lookupValue: string;
	catalogId: string;
	langId: string;
	physicalStoreId: string;
	profileName?: string;
	marketId?: string;
	stLocId?: string;
	contractId?: string;
	currency?: string;
}) => {
	const query: {
		storeId: string;
		catalogId: string;
		[key: string]: string | boolean | (string | number)[];
	} = {
		storeId,
		[lookupKey]: [lookupValue],
		catalogId,
		langId,
		physicalStoreId,
		...(profileName && { profileName }),
		...(marketId && { marketId }),
		...(stLocId && { stLocId }),
		...(contractId && { contractId }),
		...(currency && { currency }),
	};
	return query;
};
// This method help to verify if the attachments exist in SSR
export const verifyAttachments = async (filteredAttachments: Attachment[]) => {
	const results = await Promise.all(
		filteredAttachments.map(async (att) => {
			try {
				// Ignore videos
				if (att.usage === MEDIA) {
					return att;
				}
				// Validate Images and Documents
				const res = await fetch(`${CDN_BASE_URL}/${att.identifier}`, {
					method: 'HEAD',
				});
				return res.ok ? att : null;
			} catch {
				console.log('Fetch attachment failed ');
				return null;
			}
		})
	);

	// Filter out nulls (non-existing images)
	const updatedAttachments = results.filter((att) => att !== null);
	return updatedAttachments;
};
export const fetcher =
	(pub: boolean, context?: GetServerSidePropsContext) =>
	async (
		query: {
			storeId: string;
			catalogId: string;
			[key: string]: string | boolean | (string | number)[];
		},
		params: RequestParams
	): Promise<ProductFetchType | undefined> => {
		const response = await productFetcher(pub, context)(query, params);
		const product = extractContentsArray(response).at(0);
		// let updatedProduct = cloneDeep(product);
		// const validateAttachments = product?.seo?.href?.includes(context?.params?.path?.[1]);
		// This attachments validation applies only for the PDP main call
		/* if (validateAttachments && product?.attachments?.length > 0) {
			const updatedAttachments = await verifyAttachments(product?.attachments);
			updatedProduct = {
				...product,
				attachments: updatedAttachments,
			};
		} */
		return response ? { product: product ? mapProductData(product) : null } : response;
	};

export const getProductByKeyType = async (
	cache: Cache,
	lookupKey: string,
	lookupValue: string,
	context: GetServerSidePropsContext,
	profileName: string = SearchProfiles.HDM_FIND_PRODUCT_BY_PART_NUMBER_DETAILS,
	marketId?: string,
	stLocId?: string
) => {
	const settings = await getSettings(cache, context);
	const user = await getUser(cache, context);
	const { localeName: locale } = await getStoreLocale({ cache, context });
	const routes = await getLocalization(cache, locale, 'Routes');
	const { storeId, langId, defaultCatalogId: catalogId } = getServerSideCommon(settings, context);
	const physicalStoreId = getPhysicalStoreId(context);
	const query = getProductSWRKey({
		storeId,
		lookupKey,
		lookupValue,
		catalogId,
		langId,
		physicalStoreId,
		profileName,
		marketId,
		stLocId,
		...getContractIdParamFromContext(user?.context),
		...getCurrencyParamFromContext(user?.context),
	});
	const key = unstableSerialize([shrink(query), DATA_KEY]);
	const params = constructRequestParamsWithPreviewToken({ context, settings, routes });
	const cacheScope = getServerCacheScope(context, user.context);
	// a blank lookup value is rejected by the product service (INVALID_PARAMETER_VALUE), which
	// the fetcher turned into `undefined` anyway: resolve to that without the call
	const value =
		(cache.get(key, cacheScope) as Promise<ProductFetchType | undefined>) ||
		(hasLookupValue(lookupValue)
			? fetcher(false, context)(query, params)
			: Promise.resolve(undefined));
	cache.set(key, value, cacheScope);
	return (await value)?.product ?? undefined;
};

export const getProduct = async (
	map: Cache,
	partNumber: string,
	context: GetServerSidePropsContext,
	profileName?: string,
	marketId?: string,
	stLocId?: string
) =>
	await getProductByKeyType(map, 'partNumber', partNumber, context, profileName, marketId, stLocId);
